import {
  MutateFunction,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { FirstParameter, SecondParameter } from "@/openapi/types";
import type request from "@/openapi/request";
import { QueryKey } from "@tanstack/query-core";
import { InfiniteData } from "@tanstack/query-core/build/modern";
import { calculateExpression } from "@/utils/math";
import {
  postBaobPcComplaintClassifyList,
  postBaobPcComplaintRecordSubmit,
  postBaobPcComplaintRecordList,
  postBaobPcComplaintRecordDetail,
  postBaobPcComplaintRecordScore,
} from "@/openapi/baob/pc.complaint/api.client.axios";

export const useComplaintClassifyControllerGetList = (options: {
  query?: Partial<
    UseQueryOptions<
      ReturnType<typeof postBaobPcComplaintClassifyList>,
      Error,
      Awaited<ReturnType<typeof postBaobPcComplaintClassifyList>>
    >
  >;
  params: FirstParameter<typeof postBaobPcComplaintClassifyList>;
  request?: SecondParameter<typeof request>;
}) => {
  return useQuery({
    queryKey: [`/baob/pc.complaint/classifyList`, options.params],
    queryFn: () =>
      postBaobPcComplaintClassifyList(options.params, options?.request),
    ...options?.query,
  });
};

export const usePostBaobPcComplaintRecordSubmit = (options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBaobPcComplaintRecordSubmit>>,
    Error,
    FirstParameter<typeof postBaobPcComplaintRecordSubmit>
  >;
  request?: SecondParameter<typeof request>;
}) => {
  const mutationFn: MutateFunction<
    Awaited<ReturnType<typeof postBaobPcComplaintRecordSubmit>>,
    Error,
    FirstParameter<typeof postBaobPcComplaintRecordSubmit>
  > = (params: FirstParameter<typeof postBaobPcComplaintRecordSubmit>) => {
    return postBaobPcComplaintRecordSubmit(params, options?.request);
  };

  return useMutation({ mutationFn, ...options?.mutation });
};

export const usePostBaobPcComplaintRecordList = (options: {
  query?: Omit<
    UseInfiniteQueryOptions<
      Awaited<ReturnType<typeof postBaobPcComplaintRecordList>>,
      Error,
      InfiniteData<Awaited<ReturnType<typeof postBaobPcComplaintRecordList>>>,
      Awaited<ReturnType<typeof postBaobPcComplaintRecordList>>,
      QueryKey,
      FirstParameter<typeof postBaobPcComplaintRecordList>
    >,
    "queryKey" | "initialPageParam" | "getNextPageParam"
  >;
  params: FirstParameter<typeof postBaobPcComplaintRecordList>;
  request?: SecondParameter<typeof request>;
}) => {
  return useInfiniteQuery({
    queryKey: [`/baob/pc.complaint/recordList`],
    queryFn: ({ pageParam }) =>
      postBaobPcComplaintRecordList(pageParam, options?.request),
    initialPageParam: options.params,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (
        lastPage.data.total <=
        Number(
          calculateExpression(
            `${lastPageParam.pageNumber} * ${lastPageParam.pageSize}`,
          ),
        )
      ) {
        return undefined;
      }
      // 返回下一页的页码
      return {
        pageNumber: Number(lastPageParam.pageNumber) + 1,
        pageSize: lastPageParam.pageSize,
      };
    },
    ...options?.query,
  });
};

export const usePostBaobPcComplaintRecordDetail = (options: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof postBaobPcComplaintRecordDetail>>,
      Error,
      Awaited<ReturnType<typeof postBaobPcComplaintRecordDetail>>
    >
  >;
  params: FirstParameter<typeof postBaobPcComplaintRecordDetail>;
  request?: SecondParameter<typeof request>;
}) => {
  return useQuery({
    queryKey: [`/baob/pc.complaint/recordDetail`, options.params],
    queryFn: () =>
      postBaobPcComplaintRecordDetail(options.params, options?.request),
    ...options?.query,
  });
};

export const usePostBaobPcComplaintRecordScore = (options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postBaobPcComplaintRecordScore>>,
    Error,
    FirstParameter<typeof postBaobPcComplaintRecordScore>
  >;
  request?: SecondParameter<typeof request>;
}) => {
  const mutationFn: MutateFunction<
    Awaited<ReturnType<typeof postBaobPcComplaintRecordScore>>,
    Error,
    FirstParameter<typeof postBaobPcComplaintRecordScore>
  > = (params: FirstParameter<typeof postBaobPcComplaintRecordScore>) => {
    return postBaobPcComplaintRecordScore(params, options?.request);
  };

  return useMutation({ mutationFn, ...options?.mutation });
};
